import React, { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Sparkles, Flower2 } from "lucide-react";
import Envelope from "@/components/Envelope";
import PetalRain from "@/components/PetalRain";
import MusicToggle from "@/components/MusicToggle";
import FloralAccent from "@/components/FloralAccent";

const REASONS = [
    {
        emoji: "☀️",
        title: "The way you smile",
        text: "Your smile turns even my grayest days into golden ones. I could spend forever chasing that little curve of your lips.",
    },
    {
        emoji: "🌸",
        title: "Your soft heart",
        text: "The gentleness in the way you love people, animals, and the little things — it teaches me how to be kinder every day.",
    },
    {
        emoji: "💫",
        title: "The way you listen",
        text: "You listen like every word I say matters. It makes me feel safe, seen, and completely at home.",
    },
    {
        emoji: "🌿",
        title: "Your laugh",
        text: "It's my favorite sound in the entire world. I'd trade a hundred symphonies for one of your giggles.",
    },
    {
        emoji: "🕊️",
        title: "How you love me back",
        text: "Not loudly — but in the way you remember my little habits, my off days, my favorite everything. I feel it in every small thing.",
    },
    {
        emoji: "🌷",
        title: "Your dreams",
        text: "The way your eyes light up when you talk about the things you want to do — I want to build every one of them with you.",
    },
    {
        emoji: "🍯",
        title: "The way you hold my hand",
        text: "Like the world could end and you'd still be exactly where you are. That's the kind of steady I never knew I needed.",
    },
    {
        emoji: "🌙",
        title: "Just… you",
        text: "Every quirk, every mood, every random 3 AM thought. I love the whole you. Not a version. Not a highlight reel. You.",
    },
];

const PORTRAITS = [
    {
        url: "/images/portrait-1.jpeg",
        caption: "the day I fell a little harder",
        rotate: "-4deg",
    },
    {
        url: "/images/portrait-2.jpeg",
        caption: "your golden hour, always",
        rotate: "3deg",
    },
    {
        url: "/images/portrait-3.jpg",
        caption: "my favorite person",
        rotate: "-2deg",
    },
];

const HERO_FLORAL =
    "https://images.unsplash.com/photo-1578972497170-bfc780c65f65?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80";

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    }),
};

const LoveLetter = () => {
    const [surprise, setSurprise] = useState(0);

    return (
        <div className="relative min-h-screen paper-texture overflow-hidden">
            <PetalRain trigger={surprise} count={50} />
            <MusicToggle />

            {/* Ambient corner florals */}
            <FloralAccent
                variant="sprig"
                className="hidden sm:block absolute -top-6 -left-4 w-52 opacity-70 animate-sway"
            />
            <FloralAccent
                variant="wildflower"
                className="hidden sm:block absolute top-24 right-4 w-40 opacity-80 animate-float-slow"
            />
            <FloralAccent
                variant="leaves"
                className="hidden md:block absolute top-1/3 left-2 w-28 opacity-60"
            />
            <FloralAccent
                variant="rose"
                className="hidden md:block absolute bottom-32 right-8 w-40 opacity-80 animate-float-slow"
            />

            {/* HERO */}
            <section
                data-testid="hero-section"
                className="relative pt-24 sm:pt-32 pb-20 px-6"
            >
                <div className="max-w-5xl mx-auto text-center relative z-10">
                    <motion.p
                        initial="hidden"
                        animate="show"
                        variants={fadeUp}
                        className="font-hand text-2xl sm:text-3xl text-dusty-rose-deep"
                        data-testid="hero-eyebrow"
                    >
                        a little corner of the internet — for you
                    </motion.p>

                    <motion.h1
                        initial="hidden"
                        animate="show"
                        custom={1}
                        variants={fadeUp}
                        className="mt-4 font-serif text-5xl sm:text-7xl lg:text-8xl leading-[1.05] tracking-tight text-cocoa"
                        data-testid="hero-title"
                    >
                        To{" "}
                        <span className="italic text-dusty-rose-deep">
                            Lakshita
                        </span>
                        ,
                        <br />
                        my whole heart.
                    </motion.h1>

                    <motion.p
                        initial="hidden"
                        animate="show"
                        custom={2}
                        variants={fadeUp}
                        className="mt-8 max-w-2xl mx-auto font-body text-cocoa-soft text-base sm:text-lg leading-relaxed"
                    >
                        I built this tiny garden of words and flowers because I
                        wanted to say something I say too often and still not
                        enough — I love you. Wildly. Softly. Every day. Scroll
                        gently, my love. It's all for you.
                    </motion.p>

                    <motion.div
                        initial="hidden"
                        animate="show"
                        custom={3}
                        variants={fadeUp}
                        className="mt-10 flex items-center justify-center gap-3"
                    >
                        <a
                            href="#letter"
                            data-testid="scroll-to-letter"
                            className="inline-flex items-center gap-2 rounded-full bg-dusty-rose text-white px-8 py-4 font-serif text-lg tracking-wide hover:bg-dusty-rose-deep transition-colors shadow-lg shadow-dusty-rose/30"
                        >
                            <Heart className="w-4 h-4" fill="white" />
                            Read your letter
                        </a>
                        <a
                            href="#reasons"
                            className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur border border-dusty text-cocoa px-6 py-4 font-serif text-lg tracking-wide hover:bg-white transition-colors"
                        >
                            Reasons I love you
                        </a>
                    </motion.div>
                </div>

                {/* Hero floral collage */}
                <div className="relative mt-16 max-w-5xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.6, duration: 1.1 }}
                        className="mx-auto w-40 sm:w-56 aspect-square rounded-full overflow-hidden border-8 border-white polaroid-shadow"
                    >
                        <img
                            src={HERO_FLORAL}
                            alt="soft pink roses"
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                    </motion.div>
                    <div className="absolute inset-x-0 -bottom-8 flex justify-center">
                        <span className="font-hand text-2xl text-dusty-rose-deep">
                            ♡ 12.2025 ♡
                        </span>
                    </div>
                </div>
            </section>

            {/* ENVELOPE / LETTER */}
            <section
                id="letter"
                data-testid="letter-section"
                className="relative px-6 py-24 sm:py-32"
            >
                <div className="max-w-4xl mx-auto flex flex-col items-center">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="h-px w-10 bg-dusty-rose/50" />
                        <Sparkles className="w-4 h-4 text-dusty-rose-deep" />
                        <p className="font-hand text-2xl text-dusty-rose-deep">
                            a letter, just for you
                        </p>
                        <Sparkles className="w-4 h-4 text-dusty-rose-deep" />
                        <span className="h-px w-10 bg-dusty-rose/50" />
                    </div>

                    <Envelope greeting="My Dearest Lakshita,">
                        <p>
                            From the very first day, something in me quietly
                            understood — you were the person my heart had been
                            practicing for. You didn't just walk into my life,
                            Lakshita. You{" "}
                            <span className="not-italic font-hand text-2xl text-dusty-rose-deep">
                                bloomed
                            </span>{" "}
                            into it, like the softest spring after a long,
                            waiting winter.
                        </p>
                        <p className="mt-5">
                            You are my morning coffee and my late-night
                            thoughts. You are the reason I look forward to
                            ordinary Tuesdays and the reason I still get shy
                            when your name flashes on my phone. Loving you
                            doesn't feel like a choice — it feels like the most
                            natural, most obvious thing my heart has ever done.
                        </p>
                        <p className="mt-5">
                            I made this little garden of words because I wanted
                            to give you something that couldn't wilt. Every
                            petal here is a small confession: I love you today,
                            I loved you yesterday, and I have already decided —
                            quietly and completely — to love you tomorrow too.
                        </p>
                    </Envelope>
                </div>
            </section>

            {/* POLAROID GALLERY */}
            <section
                data-testid="gallery-section"
                className="relative px-6 py-20 bg-gradient-to-b from-transparent via-blush/10 to-transparent"
            >
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-14">
                        <p className="font-hand text-2xl text-dusty-rose-deep">
                            little snapshots of you
                        </p>
                        <h2 className="mt-2 font-serif text-4xl sm:text-5xl text-cocoa">
                            My favorite view.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 justify-items-center">
                        {PORTRAITS.map((p, i) => (
                            <motion.figure
                                key={i}
                                initial={{ opacity: 0, y: 40, rotate: 0 }}
                                whileInView={{
                                    opacity: 1,
                                    y: 0,
                                    rotate: parseFloat(p.rotate),
                                }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ delay: i * 0.15, duration: 0.9 }}
                                whileHover={{ rotate: 0, scale: 1.03 }}
                                className="bg-white p-3 pb-10 polaroid-shadow max-w-[280px]"
                                data-testid={`polaroid-${i}`}
                            >
                                <div className="w-full aspect-[4/5] bg-dusty overflow-hidden">
                                    <img
                                        src={p.url}
                                        alt={p.caption}
                                        loading="lazy"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <figcaption className="mt-3 font-hand text-2xl text-dusty-rose-deep text-center">
                                    {p.caption}
                                </figcaption>
                            </motion.figure>
                        ))}
                    </div>

                    <p className="mt-10 text-center font-hand text-2xl text-dusty-rose-deep">
                        my whole world, in three little frames ♡
                    </p>
                </div>
            </section>

            {/* REASONS */}
            <section
                id="reasons"
                data-testid="reasons-section"
                className="relative px-6 py-24 sm:py-32"
            >
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur border border-dusty px-5 py-2 mb-6">
                            <Flower2 className="w-4 h-4 text-dusty-rose-deep" />
                            <span className="font-body text-sm text-cocoa-soft tracking-wide">
                                a small (very incomplete) list
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl sm:text-6xl text-cocoa leading-tight">
                            Reasons I love you,{" "}
                            <span className="italic text-dusty-rose-deep">
                                Lakshita
                            </span>
                        </h2>
                        <p className="mt-4 font-body text-cocoa-soft max-w-xl mx-auto">
                            There are a million more. I just picked the ones
                            that make me smile the widest.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                        {REASONS.map((r, i) => (
                            <motion.article
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{
                                    delay: (i % 3) * 0.1,
                                    duration: 0.7,
                                }}
                                data-testid={`reason-card-${i}`}
                                className={`relative bg-white/70 backdrop-blur-md border border-dusty rounded-2xl p-7 hover:-translate-y-2 hover:shadow-xl hover:shadow-blush/20 transition-all duration-300 ${
                                    i % 5 === 0 ? "sm:col-span-2 lg:col-span-1" : ""
                                }`}
                            >
                                <div className="absolute -top-4 -left-2 text-3xl select-none">
                                    {r.emoji}
                                </div>
                                <div className="absolute top-4 right-4 font-hand text-2xl text-dusty-rose/60">
                                    №{String(i + 1).padStart(2, "0")}
                                </div>
                                <h3 className="mt-2 font-serif text-2xl text-cocoa">
                                    {r.title}
                                </h3>
                                <p className="mt-3 font-body text-cocoa-soft leading-relaxed">
                                    {r.text}
                                </p>
                            </motion.article>
                        ))}
                    </div>
                </div>
            </section>

            {/* SURPRISE */}
            <section
                data-testid="surprise-section"
                className="relative px-6 py-24"
            >
                <div className="max-w-3xl mx-auto text-center bg-gradient-to-br from-ivory to-dusty/40 border border-dusty rounded-[2rem] px-8 sm:px-14 py-16 relative overflow-hidden">
                    <FloralAccent
                        variant="rose"
                        className="absolute -top-8 -left-8 w-36 opacity-70"
                    />
                    <FloralAccent
                        variant="wildflower"
                        className="absolute -bottom-6 -right-6 w-32 opacity-70"
                    />

                    <p className="font-hand text-2xl text-dusty-rose-deep">
                        one more thing…
                    </p>
                    <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-cocoa">
                        Would you like a little surprise?
                    </h2>
                    <p className="mt-5 font-body text-cocoa-soft">
                        Tap the button. Just trust me ♡
                    </p>

                    <button
                        type="button"
                        onClick={() => setSurprise((s) => s + 1)}
                        data-testid="surprise-button"
                        className="mt-8 inline-flex items-center gap-2 rounded-full bg-dusty-rose text-white px-10 py-5 font-serif text-xl tracking-wide hover:bg-dusty-rose-deep transition-colors shadow-xl shadow-dusty-rose/30 hover:-translate-y-0.5"
                    >
                        <Heart className="w-5 h-5" fill="white" />
                        Click for a surprise
                    </button>

                    <p className="mt-6 font-hand text-xl text-dusty-rose-deep">
                        (yes, you can press it as many times as you want)
                    </p>
                </div>
            </section>

            {/* CLOSING */}
            <section
                data-testid="closing-section"
                className="relative px-6 py-28 text-center"
            >
                <FloralAccent
                    variant="leaves"
                    className="mx-auto w-32 opacity-80"
                />
                <h2 className="mt-6 font-serif text-4xl sm:text-6xl text-cocoa italic">
                    I love you, Lakshita.
                </h2>
                <p className="mt-4 font-hand text-3xl text-dusty-rose-deep">
                    today, tomorrow, and every quiet forever after.
                </p>
                <p className="mt-10 font-body text-sm text-cocoa-light">
                    made with too much love · just for you
                </p>
            </section>
        </div>
    );
};

export default LoveLetter;
